import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivityLocationDisplayComponent } from '@curbnturf/activity-component/src/lib/activity-location-display/activity-location-display.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import {
  ActivityLocation,
  ActivityType,
  ActivityTypes,
  IActivityLocation,
  LocationCategories,
  LocationCategory,
} from '@curbnturf/entities';
import { capitalize } from '@curbnturf/helpers';
import { IonicModule, ModalController } from '@ionic/angular';

interface IGroup {
  distance: number;
  type: string;
  typeLabel: string;
  activities: IActivityLocation[];
  open?: boolean;
}

@Component({
  selector: 'curbnturf-offsite-activity-display',
  templateUrl: './offsite-activity-display.component.html',
  styleUrls: ['./offsite-activity-display.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    MiscSvgComponent,
    NgFor,
    NgIf,
    SiteSvgsComponent,
  ],
})
export class OffsiteActivityDisplayComponent implements OnChanges {
  @Input() activities: IActivityLocation[] = [];
  @Input() set selectedId(id: number) {
    const selectedActivity = this.activities?.find((activity) => activity.id === id);

    this.openModal(selectedActivity);
  }
  offsiteGroup: IGroup[] = [];

  currentActivity?: IActivityLocation;

  constructor(private modalController: ModalController) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activities && changes.activities.currentValue) {
      const offsiteGroup = this.activities.reduce(
        (groups, activity: IActivityLocation) => {
          const concreteActivity = new ActivityLocation({ ...activity });
          LocationCategories.forEach((type) => {
            if (concreteActivity[`category${capitalize(type)}`]) {
              groups = this.addToGroup(
                groups,
                activity,
                ` ${type}`, // space added to float to the top
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (LocationCategory as any)[type],
              );
            }
          });

          ActivityTypes.forEach((type) => {
            if (type !== 'custom') {
              if (concreteActivity[`activity${capitalize(type)}`]) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                groups = this.addToGroup(groups, activity, type, (ActivityType as any)[type]);
              }
            }
          });

          if (activity.customActivity) {
            groups = this.addToGroup(groups, activity, activity.customActivity, activity.customActivity);
          }

          return groups;
        },
        {} as { [id: string]: IGroup },
      );

      this.offsiteGroup = Object.keys(offsiteGroup)
        .map((key) => {
          const group = offsiteGroup[key];
          group.type = key;

          return group;
        })
        .sort((groupA, groupB) => {
          return groupA.type.localeCompare(groupB.type);
        });
    }
  }

  openGroup(group: IGroup) {
    group.open = !group.open;
  }

  async openModal(activity?: IActivityLocation) {
    if (activity) {
      const modal = await this.modalController.create({
        component: ActivityLocationDisplayComponent,
        componentProps: {
          activity,
        },
      });
      await modal.present();
    }
  }

  groupActivityExpandable(group: IGroup): boolean {
    return !!group.activities.find((activity) => {
      const expandable = !!activity.description || !!activity.url || (activity.photos && activity.photos.length > 0);

      return expandable || false;
    });
  }

  // this function mutates groups.
  private addToGroup(
    groups: { [id: string]: IGroup },
    activity: IActivityLocation,
    type: string,
    typeLabel: string,
  ): { [id: string]: IGroup } {
    let distance = activity.distance;
    if (typeof distance === 'string') {
      distance = parseFloat(distance);
    }

    if (distance || distance === 0) {
      if (!groups[type]) {
        groups[type] = { distance, activities: [], type, typeLabel };
      }

      groups[type].activities.push(activity);

      if (groups[type].distance > distance) {
        groups[type].distance = distance;
      }
    }

    return groups;
  }
}
